import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { PROJECTS } from "../../data/portfolioData";
import { ArrowUpRightIcon } from "../ui/Icons";
import Reveal from "../ui/Reveal";
import { EASE, cardInView, cardVariants } from "../ui/cardMotion";

const DEVICON = "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons";
const TECH_LABELS = {
  python: "Python",
  pytorch: "PyTorch",
  numpy: "NumPy",
  pandas: "pandas",
  jupyter: "Jupyter",
  fastapi: "FastAPI",
  react: "React",
  docker: "Docker",
  flask: "Flask",
  mongodb: "MongoDB",
  huggingface: "Hugging Face",
  opencv: "OpenCV",
  matplotlib: "Matplotlib",
};

const TechChip = ({ name }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-primary/60 px-2.5 py-1 text-xs text-ink/75">
      {!failed && (
        <img
          src={`${DEVICON}/${name}/${name}-original.svg`}
          alt=""
          className="w-3.5 h-3.5"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      )}
      {TECH_LABELS[name] || name}
    </span>
  );
};

// Screenshot in a tilted browser window, sitting directly on the card.
// Idle: angled like a card on a desk. Hover: it straightens, follows the cursor in 3D and grows a little.
const IDLE = { rx: 6, ry: 14, rz: -2 };

const mockupVariants = {
  hidden: { opacity: 0, y: 40, rotateY: 28 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    rotateY: 0,
    transition: { duration: 1, ease: EASE, delay: delay + 0.15 },
  }),
};

const ProjectImage = ({ project, featured, delay }) => {
  const frameRef = useRef(null);
  const href = project.demo || project.link;

  // Pointer position within the frame, -0.5..0.5, and a 0..1 hover blend.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const hover = useMotionValue(0);
  const spring = { stiffness: 180, damping: 18, mass: 0.5 };
  const sx = useSpring(px, spring);
  const sy = useSpring(py, spring);
  const sh = useSpring(hover, { stiffness: 160, damping: 20 });

  const rotateY = useTransform([sx, sh], ([x, h]) => IDLE.ry * (1 - h) + x * 16 * h);
  const rotateX = useTransform([sy, sh], ([y, h]) => IDLE.rx * (1 - h) - y * 12 * h);
  const rotateZ = useTransform(sh, [0, 1], [IDLE.rz, 0]);
  const x = useTransform([sx, sh], ([v, h]) => v * 18 * h);
  const y = useTransform([sy, sh], ([v, h]) => v * 12 * h);
  const scale = useTransform(sh, [0, 1], [1, 1.06]);

  const onMove = (e) => {
    const r = frameRef.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    hover.set(0);
    px.set(0);
    py.set(0);
  };

  const Frame = href ? "a" : "div";
  const frameProps = href
    ? { href, target: "_blank", rel: "noopener noreferrer", "aria-label": `${project.demoLabel || "Open"}: ${project.title}` }
    : {};

  return (
    <Frame
      {...frameProps}
      ref={frameRef}
      onMouseMove={onMove}
      onMouseEnter={() => hover.set(1)}
      onMouseLeave={onLeave}
      className={`relative block [perspective:1200px] ${
        featured ? "aspect-[2/1] lg:aspect-auto lg:h-full lg:min-h-[240px]" : "aspect-[2/1]"
      } ${href ? "cursor-pointer" : ""}`}
    >
      <div className="absolute inset-0 flex items-center justify-center [transform-style:preserve-3d]">
        <motion.div
          variants={mockupVariants}
          custom={delay}
          className={featured ? "w-[78%]" : "w-[76%]"}
        >
          <motion.div
            style={{ rotateX, rotateY, rotateZ, x, y, scale }}
            className="rounded-lg overflow-hidden bg-card border border-ink/10 shadow-[0_22px_45px_-12px_rgb(0_0_0/0.4),0_10px_20px_-10px_rgb(0_0_0/0.3)] will-change-transform"
          >
            <div className="h-5 bg-[#211d1b] flex items-center gap-1 px-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff5f57]" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#febc2e]" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#28c840]" />
            </div>
            <img
              src={project.image}
              alt={project.title}
              loading="lazy"
              className="block w-full aspect-[16/9] object-cover object-top"
            />
          </motion.div>
        </motion.div>
      </div>
    </Frame>
  );
};

const LinkButton = ({ href, primary, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-semibold transition-colors ${
      primary
        ? "bg-ink text-primary hover:bg-accent"
        : "border border-ink/15 text-ink hover:border-accent hover:text-accent"
    }`}
  >
    {children}
    <ArrowUpRightIcon size={14} />
  </a>
);

const ProjectCard = ({ project, index, featured = false }) => {
  const delay = featured ? 0 : (index % 2 === 0 ? 0.12 : 0);

  return (
    <motion.article
      className={`group relative block rounded-[1.75rem] bg-card border border-ink/10 p-2.5 shadow-[0_1px_0_rgb(0_0_0/0.02)] hover:shadow-[0_24px_60px_rgb(0_0_0/0.10)] hover:border-accent/30 transition-[box-shadow,border-color] duration-500 ${
        featured ? "lg:col-span-2 lg:grid lg:grid-cols-5 lg:gap-3" : ""
      }`}
      variants={cardVariants}
      custom={delay}
      {...cardInView}
    >
      <div className={featured ? "lg:col-span-3" : ""}>
        <ProjectImage project={project} featured={featured} delay={delay} />
      </div>

      <div className={`flex flex-col px-2.5 pt-4 pb-2.5 ${featured ? "lg:col-span-2 lg:justify-center lg:px-6" : ""}`}>
        <div className="flex items-center justify-between gap-4 text-xs uppercase tracking-[0.18em] text-ink/50">
          <span>{String(index + 1).padStart(2, "0")}</span>
          <span className="text-accent">{project.category}</span>
        </div>

        <h3 className={`mt-3 font-bold leading-snug ${featured ? "text-xl lg:text-2xl" : "text-lg lg:text-xl"}`}>
          {project.title}
        </h3>

        <p className={`text-ink/65 leading-relaxed mt-2 ${featured ? "text-sm lg:text-base" : "text-sm"}`}>{project.description}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          {project.tech.map((t) => (
            <TechChip key={t} name={t} />
          ))}
        </div>

        {(project.demo || project.link) && (
          <div className="flex flex-wrap gap-2 mt-5">
            {project.demo && (
              <LinkButton href={project.demo} primary>
                {project.demoLabel || "Live demo"}
              </LinkButton>
            )}
            {project.link && (
              <LinkButton href={project.link} primary={!project.demo}>
                {project.linkLabel || "Source"}
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </motion.article>
  );
};

const Projects = () => {
  const [featured, ...rest] = PROJECTS;
  return (
    <section id="projects" className="pt-24 pb-20">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 max-w-[60rem] mx-auto">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-ink/50 mb-4">
            Selected work · {String(PROJECTS.length).padStart(2, "0")}
          </p>
          <h2 className="font-bold text-4xl md:text-5xl leading-tight">
            A small selection of <br className="hidden md:block" />
            <span className="text-accent">recent projects</span>
          </h2>
        </div>
        <p className="text-ink/60 max-w-xs leading-relaxed text-sm md:text-base">
          From reinforcement learning to medical imaging: models I've trained, and the products I built around them.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 max-w-[60rem] mx-auto">
        <ProjectCard project={featured} index={0} featured />
        {rest.map((project, i) => (
          <ProjectCard key={project.title} project={project} index={i + 1} />
        ))}
      </div>
    </section>
  );
};

export default Projects;
